import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Star, MapPin, Phone, Mail, ArrowLeft, Building2 } from "lucide-react";

export default function VendorDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [v, setV] = useState(null);
  const [myProps, setMyProps] = useState([]);
  const [propId, setPropId] = useState("");
  const [planName, setPlanName] = useState("Standard");
  const [monthlyFee, setMonthlyFee] = useState(0);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const nav = useNavigate();

  const load = async () => {
    try { const { data } = await api.get(`/vendors/${id}`); setV(data); }
    catch { toast.error("Vendor not found"); nav("/vendors"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    if (user?.role === "owner") {
      api.get("/properties", { params: { owner_id: user.id, status: "all" } }).then(r => {
        setMyProps(r.data); if (r.data[0]) setPropId(r.data[0].id);
      }).catch(()=>{});
    }
  }, [user]);

  const subscribe = async () => {
    if (!propId) { toast.error("Select a property first"); return; }
    try {
      await api.post("/vendors/subscribe", { vendor_id: id, property_id: propId, plan_name: planName, monthly_fee: parseFloat(monthlyFee) || 0 });
      toast.success("Subscribed!"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const submitReview = async () => {
    if (!reviewText.trim()) return;
    try {
      await api.post("/vendors/reviews", { vendor_id: id, rating: reviewRating, comment: reviewText });
      toast.success("Review posted!"); setReviewText(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  if (!v) return <div className="max-w-7xl mx-auto px-5 py-20">Loading...</div>;
  return (
    <div className="paper min-h-[80vh]" data-testid="vendor-detail-page">
      <div className="max-w-5xl mx-auto px-5 md:px-10 py-10">
        <Link to="/vendors" className="inline-flex items-center gap-1 text-sm text-[#484848] mb-3 hover:underline"><ArrowLeft size={14}/> Back to vendors</Link>
        <div className="brut-card overflow-hidden bg-white">
          {v.images?.[0] && <div className="h-64 brut-border-b overflow-hidden"><img src={v.images[0]} alt="" className="w-full h-full object-cover"/></div>}
          <div className="p-6">
            <div className="flex flex-wrap justify-between gap-3">
              <div>
                <span className="brut-tag lime !text-[10px] uppercase">{v.category}</span>
                <h1 className="font-display font-black text-3xl mt-2">{v.business_name}</h1>
                <p className="text-sm text-[#484848] mt-1 flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1"><MapPin size={14}/>{v.city}{v.pincode ? ` · ${v.pincode}` : ""}</span>
                  <span className="flex items-center gap-1"><Phone size={14}/>{v.phone}</span>
                  <span className="flex items-center gap-1"><Mail size={14}/>{v.email}</span>
                </p>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-1"><Star size={16} className="fill-[#FFC857]"/> <strong className="text-xl">{v.rating || 0}</strong></div>
                <div className="text-xs text-[#484848]">{v.review_count || 0} review(s)</div>
              </div>
            </div>

            <h2 className="font-display font-black text-lg mt-6">About</h2>
            <p className="text-[#484848] mt-1 whitespace-pre-wrap">{v.description || "No description provided."}</p>

            {v.services?.length > 0 && (
              <>
                <h2 className="font-display font-black text-lg mt-6">Services & pricing</h2>
                <div className="grid sm:grid-cols-2 gap-2 mt-2">
                  {v.services.map((s, i) => (
                    <div key={i} className="brut-border p-3 flex justify-between text-sm">
                      <span>{s.name}</span>
                      <strong>₹{s.price}{s.unit ? ` / ${s.unit}` : ""}</strong>
                    </div>
                  ))}
                </div>
              </>
            )}

            {user?.role === "owner" && (
              <div className="mt-8 brut-card p-5 bg-[#ECFAF1]">
                <h3 className="font-display font-black text-xl">Subscribe a property</h3>
                <p className="text-sm text-[#484848] mt-1">Plug this vendor into one of your PGs.</p>
                <div className="grid sm:grid-cols-3 gap-3 mt-4">
                  <div><label className="label-eyebrow">Property</label>
                    <select className="brut-input mt-1" value={propId} onChange={(e)=>setPropId(e.target.value)} data-testid="sub-prop">
                      {myProps.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                    </select>
                  </div>
                  <div><label className="label-eyebrow">Plan name</label>
                    <input className="brut-input mt-1" value={planName} onChange={(e)=>setPlanName(e.target.value)}/></div>
                  <div><label className="label-eyebrow">Monthly fee (₹)</label>
                    <input type="number" className="brut-input mt-1" value={monthlyFee} onChange={(e)=>setMonthlyFee(e.target.value)}/></div>
                </div>
                <button className="brut-btn mt-4" onClick={subscribe} data-testid="sub-confirm">Subscribe</button>
              </div>
            )}

            <h2 className="font-display font-black text-lg mt-8">Reviews ({v.reviews?.length || 0})</h2>
            {user && (
              <div className="brut-card p-4 mt-3 bg-[#FDFCF8]">
                <div className="flex items-center gap-2 mb-2">
                  <label className="label-eyebrow">Rating</label>
                  <select className="brut-input !py-1 !w-20" value={reviewRating} onChange={(e)=>setReviewRating(parseInt(e.target.value))} data-testid="review-rating">
                    {[5,4,3,2,1].map(n => <option key={n} value={n}>{n} ⭐</option>)}
                  </select>
                </div>
                <textarea className="brut-input min-h-[60px]" placeholder="Share your experience…" value={reviewText} onChange={(e)=>setReviewText(e.target.value)} data-testid="review-text"/>
                <button className="brut-btn mt-2" onClick={submitReview} data-testid="review-submit">Post review</button>
              </div>
            )}
            <div className="space-y-2 mt-3">
              {(v.reviews || []).map(r => (
                <div key={r.id} className="brut-border p-3 bg-white">
                  <div className="flex justify-between text-sm">
                    <strong>{r.user_name}</strong>
                    <span><Star size={12} className="inline fill-[#FFC857]"/> {r.rating}</span>
                  </div>
                  <p className="text-sm text-[#484848] mt-1">{r.comment}</p>
                  <div className="text-xs text-[#484848] mt-1">{r.created_at?.slice(0, 10)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
